import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
   getAnalyticsAging,
   getAnalyticsBreakdown,
   getAnalyticsForecast,
   getAnalyticsSummary,
   getAnalyticsVintage,
} from '../../../../services/finance/billingAnalyticsApi';
import {
   readApiError,
   financeErrorMessage,
} from '../../utils/apiError';
import {
   BREAKDOWN_LIMIT,
   FORECAST_HORIZONS,
   vintageMonthsFor,
} from '../constants';

// ---------------------------------------------------------------------------
// As 5 chamadas de /billing/analytics/* — INDEPENDENTES e PARALELAS.
//
// Nada de cascata de useEffect (0084 §9): cada endpoint responde em dezenas de
// ms e nenhum depende do resultado do outro. Cada bloco tem loading e erro
// PRÓPRIOS — um 500 no vintage não pode apagar os tiles que já carregaram.
//
// Reatividade granular, como o design especifica:
//   · recorte global (período/origem/CNPJ/lente) → refaz as 5
//   · `by`             → refaz só o breakdown
//   · `threshold_dias` → refaz só o vintage
//
// Corrida: cada bloco tem um contador de sequência. Resposta de um recorte
// antigo que chega atrasada é DESCARTADA — sem isso, clicar dois chips rápido
// pode deixar a tela mostrando o resultado do primeiro.
// ---------------------------------------------------------------------------

const BLOCK_KEYS = ['summary', 'aging', 'breakdown', 'forecast', 'vintage'];

const INITIAL_BLOCK = { data: null, loading: true, error: null };

function initialBlocks() {
   return BLOCK_KEYS.reduce(
      (acc, key) => ({ ...acc, [key]: { ...INITIAL_BLOCK } }),
      {},
   );
}

export default function useCreditSalesData({
   globalFilters,
   by,
   thresholdDias,
}) {
   const [blocks, setBlocks] = useState(initialBlocks);
   const [refreshToken, setRefreshToken] = useState(0);

   const seqRef = useRef({});
   // O recorte corrente para os blocos que recarregam sozinhos (breakdown e
   // vintage): eles precisam do filtro global vigente sem depender dele como
   // dependência de efeito — senão trocar de aba refaria as 5.
   const filtersRef = useRef(globalFilters);
   filtersRef.current = globalFilters;
   const byRef = useRef(by);
   byRef.current = by;
   const thresholdRef = useRef(thresholdDias);
   thresholdRef.current = thresholdDias;

   // Uma falha vira UM toast por lote, não cinco. O bloco que falhou mostra seu
   // próprio estado de erro inline; o toast só avisa que algo caiu.
   const notifiedRef = useRef(false);

   const runBlock = useCallback((key, request) => {
      const seq = (seqRef.current[key] ?? 0) + 1;
      seqRef.current[key] = seq;

      setBlocks((prev) => ({
         ...prev,
         [key]: { ...prev[key], loading: true, error: null },
      }));

      return request()
         .then((data) => {
            if (seqRef.current[key] !== seq) return; // recorte já mudou
            setBlocks((prev) => ({
               ...prev,
               [key]: { data, loading: false, error: null },
            }));
         })
         .catch((err) => {
            if (seqRef.current[key] !== seq) return;
            const apiError = readApiError(err);
            setBlocks((prev) => ({
               ...prev,
               [key]: { data: null, loading: false, error: apiError },
            }));

            // 422 que aponta um CAMPO vai inline na barra de filtros, nunca em
            // toast (FC4 §B.3): o operador precisa ver a crítica ao lado do
            // controle que a causou, não numa caixa que some sozinha. Como o
            // recorte é o mesmo nos 5 endpoints, um filtro inválido derruba os
            // 5 blocos de uma vez — cinco toasts idênticos seriam ruído puro.
            const hasFieldError = Object.keys(apiError.fieldErrors).length > 0;
            if (!hasFieldError && !notifiedRef.current) {
               notifiedRef.current = true;
               toast.error(
                  financeErrorMessage(
                     err,
                     'Não foi possível carregar os indicadores.',
                  ),
               );
            }
         });
   }, []);

   // ─── Recorte global: as 5 em paralelo ──────────────────────────────────
   useEffect(() => {
      notifiedRef.current = false;
      const filters = globalFilters;

      runBlock('summary', () => getAnalyticsSummary(filters));
      runBlock('aging', () => getAnalyticsAging(filters));
      runBlock('forecast', () =>
         getAnalyticsForecast({ ...filters, horizons: FORECAST_HORIZONS }),
      );
      runBlock('breakdown', () =>
         getAnalyticsBreakdown({
            ...filters,
            by: byRef.current,
            limit: BREAKDOWN_LIMIT,
         }),
      );
      runBlock('vintage', () =>
         getAnalyticsVintage({
            ...filters,
            thresholdDias: thresholdRef.current,
            months: vintageMonthsFor(thresholdRef.current),
         }),
      );
   }, [globalFilters, refreshToken, runBlock]);

   // ─── Só a aba do breakdown mudou ───────────────────────────────────────
   const didMountBreakdown = useRef(false);
   useEffect(() => {
      if (!didMountBreakdown.current) {
         didMountBreakdown.current = true;
         return; // o efeito global já carregou este bloco no mount
      }
      runBlock('breakdown', () =>
         getAnalyticsBreakdown({
            ...filtersRef.current,
            by,
            limit: BREAKDOWN_LIMIT,
         }),
      );
   }, [by, runBlock]);

   // ─── Só o limiar da safra mudou ────────────────────────────────────────
   const didMountVintage = useRef(false);
   useEffect(() => {
      if (!didMountVintage.current) {
         didMountVintage.current = true;
         return;
      }
      runBlock('vintage', () =>
         getAnalyticsVintage({
            ...filtersRef.current,
            thresholdDias,
            // A régua muda os meses pedidos: com 90 dias, M+1 e M+2 não podem
            // ter sinal — pedi-los devolveria uma coluna de zeros estruturais.
            months: vintageMonthsFor(thresholdDias),
         }),
      );
   }, [thresholdDias, runBlock]);

   const refresh = useCallback(() => {
      setRefreshToken((token) => token + 1);
   }, []);

   // ─── Derivados de leitura ──────────────────────────────────────────────

   // "Hoje" vem do SERVIDOR. Qualquer payload serve; o summary é o que sempre
   // existe quando há dado na tela.
   const referenceDate = useMemo(
      () =>
         blocks.summary.data?.reference_date ??
         blocks.aging.data?.as_of ??
         null,
      [blocks.summary.data, blocks.aging.data],
   );

   // `data_quality` vem em TODO payload; o do vintage traz um campo a mais
   // (`future_points_are_null`). Consolidar aqui evita o painel ter que saber
   // de qual bloco veio cada ressalva.
   const dataQuality = useMemo(() => {
      const base =
         blocks.summary.data?.data_quality ??
         blocks.aging.data?.data_quality ??
         blocks.breakdown.data?.data_quality ??
         null;
      const vintageQuality = blocks.vintage.data?.data_quality ?? null;
      if (!base && !vintageQuality) return null;
      return { ...(base ?? {}), ...(vintageQuality ?? {}) };
   }, [
      blocks.summary.data,
      blocks.aging.data,
      blocks.breakdown.data,
      blocks.vintage.data,
   ]);

   // 422 chega com o campo do filtro que ofendeu (`errors[].field`) — a barra de
   // filtros exibe inline. Erros de campo de qualquer bloco valem para a barra:
   // o recorte é o mesmo nos 5.
   const fieldErrors = useMemo(() => {
      const merged = {};
      BLOCK_KEYS.forEach((key) => {
         const entries = blocks[key].error?.fieldErrors;
         if (entries) Object.assign(merged, entries);
      });
      return merged;
   }, [blocks]);

   const isInitialLoading = BLOCK_KEYS.every(
      (key) => blocks[key].loading && !blocks[key].data,
   );

   return {
      summary: blocks.summary,
      aging: blocks.aging,
      breakdown: blocks.breakdown,
      forecast: blocks.forecast,
      vintage: blocks.vintage,
      referenceDate,
      dataQuality,
      fieldErrors,
      isInitialLoading,
      refresh,
   };
}
